/**
 * Pure calculation functions for the Topp 5 prosjekter widget (VIB-1318).
 *
 * timepris  = bf_actual / alle loggede timer (SOT «Reell timepris», Erik-låst 2026-05-15) — kun avsluttede prosjekter
 * deviation = (soldHours − usedHours) / soldHours × 100 (positive = under budget)
 * BF ranking: completed projects with deviation > 0, sorted by bf_actual desc.
 */

// VIB-1452 terminologi: `bf_actual` beholdes som datakontrakt; verdien vises
// som GEEK i topplisten.

export interface ProjectInput {
  id: string;
  name: string;
  projectCode: string | null;
  totalAmount: number | null;
  bfActual: number | null;
  projectStatus: string | null;
  customerName: string | null;
  salesOwnerNames: string[];
  projectManagerName: string | null;
}

export interface TimeprisRankEntry {
  rank: number;
  id: string;
  name: string;
  code: string;
  customerName: string;
  salesOwnerNames: string[];
  projectManagerName: string | null;
  soldHours: number;
  timeprisKr: number;
}

export interface BfRankEntry {
  rank: number;
  id: string;
  name: string;
  code: string;
  customerName: string;
  salesOwnerNames: string[];
  projectManagerName: string | null;
  deviationPct: number;
  bfActual: number;
}

/**
 * Top 5 projects by reell timepris (SOT: bf_actual / all logged hours),
 * filtered to projects with sold hours > 100 (per design).
 */
export function computeTimeprisRanking(
  projects: ProjectInput[],
  soldHoursMap: Map<string, number>,
  usedHoursMap: Map<string, number>,
  topN = 5,
): TimeprisRankEntry[] {
  const candidates: { proj: ProjectInput; soldHours: number; timeprisKr: number }[] = [];

  for (const proj of projects) {
    // Kun avsluttede prosjekter (Erik 10.07) — aktive har ikke endelig
    // bf_actual, så en løpende timepris blir misvisende i rankingen.
    if (proj.projectStatus !== 'completed') continue;
    const soldHours = soldHoursMap.get(proj.id) ?? 0;
    if (soldHours <= 100) continue;
    const bfActual = Number(proj.bfActual ?? 0);
    const loggedHours = usedHoursMap.get(proj.id) ?? 0;
    if (bfActual <= 0 || loggedHours <= 0) continue;
    candidates.push({ proj, soldHours, timeprisKr: bfActual / loggedHours });
  }

  candidates.sort((a, b) => b.timeprisKr - a.timeprisKr);

  return candidates.slice(0, topN).map(({ proj, soldHours, timeprisKr }, i) => ({
    rank: i + 1,
    id: proj.id,
    name: proj.name,
    code: proj.projectCode ?? '',
    customerName: proj.customerName ?? '',
    salesOwnerNames: proj.salesOwnerNames,
    projectManagerName: proj.projectManagerName,
    soldHours,
    timeprisKr,
  }));
}

/**
 * Top 5 completed projects by bf_actual among those under budget
 * (deviation > 0: solgte timer > brukte timer).
 */
export function computeBfRanking(
  projects: ProjectInput[],
  soldHoursMap: Map<string, number>,
  usedHoursMap: Map<string, number>,
  topN = 5,
): BfRankEntry[] {
  const candidates: { proj: ProjectInput; deviationPct: number; bfActual: number }[] = [];

  for (const proj of projects) {
    if (proj.projectStatus !== 'completed') continue;
    const bfActual = Number(proj.bfActual ?? 0);
    if (bfActual <= 0) continue;

    const soldHours = soldHoursMap.get(proj.id) ?? 0;
    if (soldHours <= 0) continue;

    const usedHours = usedHoursMap.get(proj.id) ?? 0;
    const deviationPct = ((soldHours - usedHours) / soldHours) * 100;
    if (deviationPct <= 0) continue;

    candidates.push({ proj, deviationPct, bfActual });
  }

  candidates.sort((a, b) => b.bfActual - a.bfActual);

  return candidates.slice(0, topN).map(({ proj, deviationPct, bfActual }, i) => ({
    rank: i + 1,
    id: proj.id,
    name: proj.name,
    code: proj.projectCode ?? '',
    customerName: proj.customerName ?? '',
    salesOwnerNames: proj.salesOwnerNames,
    projectManagerName: proj.projectManagerName,
    deviationPct,
    bfActual,
  }));
}
